import { useMutation, useQuery } from '@apollo/client';
import { Balance } from 'components/Balance/Balance';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import { DocumentsTab } from 'components/DocumentsTabs/DocumentsTabs';
import { MemberHeader } from 'components/MemberHeader/MemberHeader';
import { MemberTabs } from 'components/MemberTabs/MemberTabs';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Pagination } from 'components/Pagination/Pagination';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { StatusFilter } from 'components/StatusFilter/StatusFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import { useToast } from 'components/Toast/use-toast';
import dayjs from 'dayjs';
import { normalizeArray } from 'lib/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
    DEDUCT_FROM_REMAINDER,
    // EXPORT_CONSUMPTIONS,
    EXPORT_ENTRIES,
    MANUAL_TRANSACTION_CREATE,
    MANUAL_TRANSACTION_DELETE,
    MANUAL_TRANSACTION_UPDATE,
} from 'services/users/mutation';
import { USER_MANUAL_TRANSACTIONS } from 'services/users/query';
import { useUserStore } from 'stores/user.store';

import { AddConsumptionModal } from './AddConsumptionModal';
import { ConfirmConsumptionsModal } from './ConfirmConsumptionsModal';
import { DeductRemainderConfirmModal } from './DeductRemainderConfirmModal';
import { DeleteConsumptionsModal } from './DeleteConsumptionsModal';
import { MemberConsumptionsActions } from './MemberConsumptionsActions';

const PAGE_SIZE = parseInt(localStorage.getItem('pageSize') as any) || 10;
// const TYPES = ['PAYMENT', 'REFUND', 'CHARGE_BACK'];
const TYPES = ['CONSUMPTION', 'WINE', 'CATERING', 'DEBIT', 'DEPOSIT', 'CREDIT', 'MEMBERSHIP_FEE'];
const SORT = [
    { value: 'HIGHEST_AMOUNT', label: 'Highest amount' },
    { value: 'LOWEST_AMOUNT', label: 'Lowest amount' },
    { value: 'NEWEST_DATE', label: 'Newest date' },
    { value: 'OLDEST_DATE', label: 'Oldest date' },
];

const POSITIVE_TRANSACTIONS = ['DEPOSIT', 'CREDIT'];

export const MemberConsumptions = () => {
    const { user, getUser } = useUserStore(state => state);
    const [allSelected, setAllSelected] = useState(false);
    const [selectedConsumptions, setSelectedConsumptions] = useState<any>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [deductTransaction, setDeductTransaction] = useState<any>(null);
    const [isOpenDeduct, setIsOpenDeduct] = useState(false);
    const [isOpenAdd, setIsOpenAdd] = useState(false);
    const [isOpenDelete, setIsOpenDelete] = useState(false);
    // const [exportConsumptionsMutation, { loading }] = useMutation(EXPORT_CONSUMPTIONS);
    const [exportManualEntries, { loading }] = useMutation(EXPORT_ENTRIES);
    const [deductFromRemainderMutation] = useMutation(DEDUCT_FROM_REMAINDER);
    const [addTransactionMutation] = useMutation(MANUAL_TRANSACTION_CREATE);
    const [editingConsumption, setEditingConsumption] = useState<any>(null);
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [updateTransactionMutation] = useMutation(MANUAL_TRANSACTION_UPDATE);
    const [manualTransactionDelete] = useMutation(MANUAL_TRANSACTION_DELETE);

    const { t } = useTranslation('documents');
    const { t: b } = useTranslation('translation');
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const isDocument = location.pathname.includes('/documents');

    const { data, refetch } = useQuery(USER_MANUAL_TRANSACTIONS, {
        variables: { member: user?.id, first: PAGE_SIZE },
    });

    useEffect(() => {
        const search = searchParams.get('search');
        const sort = searchParams.get('sort');
        const type = searchParams.get('type')?.split(',');
        const page = searchParams.get('page') || '1';

        refetch({
            search: search,
            sort: sort,
            type: type,
            offset: (parseInt(page) - 1) * PAGE_SIZE,
        });
    }, [searchParams, refetch]);

    const selectAll = () => {
        if (allSelected) {
            setSelectedConsumptions([]);
            setAllSelected(false);
            return;
        }
        setAllSelected(true);
        setSelectedConsumptions(data?.balanceEntries?.edges.map((t: any) => t.node.id));
    };

    const selectConsumption = (id: string) => {
        if (allSelected) setAllSelected(false);
        if (selectedConsumptions.includes(id)) {
            setSelectedConsumptions(selectedConsumptions.filter((member: any) => member !== id));
            return;
        }
        setSelectedConsumptions([...selectedConsumptions, id]);
    };

    const exportConsumptions = () => {
        if (!selectedConsumptions.length) {
            setAllSelected(true);
            setSelectedConsumptions(data?.balanceEntries?.edges.map((t: any) => t.node.id));
        }
        setIsOpen(true);
    };

    const confirmExportConsumptions = async () => {
        if (!selectedConsumptions.length) return;
        const resp = await exportManualEntries({
            variables: {
                balanceEntries: selectedConsumptions,
            },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.exportManualEntries?.file) {
            window.open(resp?.data?.exportManualEntries?.file, '_blank');
            setIsOpen(false);
            setSelectedConsumptions([]);
            setAllSelected(false);
            successToast();
        }
    };

    const deductFromRemainder = (transaction: any) => {
        setDeductTransaction(transaction);
        setIsOpenDeduct(true);
    };

    const confirmDeduct = async () => {
        const resp = await deductFromRemainderMutation({
            variables: { transaction: deductTransaction.id },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.transactionDeductFromUpdate?.transaction?.id) {
            toast({
                title: t('deductFromRemainderSuccess'),
                variant: 'success',
            });
            setIsOpenDeduct(false);
            refetch();
            if (user) getUser(user.id);
        }
        setDeductTransaction(null);
    };

    const handleConsumptionAdd = async (data: any) => {
        data.amount = Number(data.amount);
        data.date = dayjs(data.date, 'DD.MM.YYYY').format('YYYY-MM-DD');
        try {
            setIsLoading(true);

            if (editingConsumption) {
                await handleEditConsumption(data);
            } else {
                await handleAddConsumption(data);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddConsumption = async (data: any) => {
        data.member = user?.id;
        const resp = await addTransactionMutation({
            variables: { input: data },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.manualBalanceEntryCreate?.balanceEntry?.id) {
            toast({
                title: t('addConsumptionSuccess'),
                variant: 'success',
            });
            setIsOpenAdd(false);
            refetch();
            if (user) getUser(user.id);
        }
    };

    const handleEditConsumption = async (data: any) => {
        const { __typename, ...updateData } = data;
        const resp = await updateTransactionMutation({
            variables: {
                id: editingConsumption.id,
                input: updateData,
            },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.manualBalanceEntryUpdate?.balanceEntry?.id) {
            toast({
                title: t('editConsumptionSuccess'),
                variant: 'success',
            });
            setIsOpenAdd(false);
            setEditingConsumption(null);
            if (user) getUser(user.id);
        }
    };

    const editConsumption = (transaction: any) => {
        console.log('editting consumption', transaction);
        setEditingConsumption(transaction);
        setIsOpenAdd(true);
    };

    const deleteConsumption = async () => {
        setIsLoading(true);
        const resp = await manualTransactionDelete({
            variables: {
                id: editingConsumption.id,
            },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.manualBalanceEntryDelete?.success) {
            toast({
                title: t('deleteConsumptionSuccess'),
                variant: 'success',
            });
            setIsOpenDelete(false);
        }
        setEditingConsumption(null);
        setIsLoading(false);
        if (user) getUser(user.id);
    };

    const handleConsumptionModalClose = () => {
        setIsOpenAdd(false);
        setEditingConsumption(null);
    };

    const successToast = () => {
        toast({
            title: t('successExportTitle'),
            variant: 'success',
        });
    };

    const failToast = (message: any) => {
        toast({
            title: t('failToastTitle'),
            description: message || t('failToastSubtitle'),
            variant: 'warning',
        });
    };

    if (!user) return null;

    return (
        <div>
            {isDocument ? (
                <>
                    <Breadcrumbs
                        crumbs={[
                            b('breadcrumbs.dashboard'),
                            b('breadcrumbs.consumptions'),
                            `${user.lastName}, ${user.firstName}  ${b('breadcrumbs.invoices')}`,
                        ]}
                    />
                    <DocumentsTab />
                    <PageTitle withBack>
                        {user.lastName}, {user.firstName}'s consumptions
                    </PageTitle>
                </>
            ) : (
                <>
                    <Breadcrumbs
                        crumbs={[
                            b('breadcrumbs.dashboard'),
                            b('breadcrumbs.members'),
                            `${user.firstName} ${user.lastName}  ${b('breadcrumbs.consumptions')}`,
                        ]}
                    />

                    <MemberHeader />
                    <MemberTabs />
                </>
            )}
            <div className="flex justify-between">
                <div className="flex gap-4 flex-wrap pb-4">
                    <SearchFilter placeholder={t('searchInvoicesPlaceholder')} />
                    <StatusFilter
                        options={TYPES}
                        label="Type"
                        filterKey="type"
                        translationKeyPrefix="transactionTypes"
                    />
                    <SortFilter options={SORT} />
                </div>
                <div className="flex gap-4">
                    <Button
                        onClick={() => exportConsumptions()}
                        variant="outline"
                        disabled={!selectedConsumptions.length}
                    >
                        {t('exportButton')}
                    </Button>
                    <Button onClick={() => setIsOpenAdd(true)}>{t('addConsumptionButton')}</Button>
                </div>
            </div>
            <Table className="border">
                <TableHeader>
                    <TableRow>
                        <TableHead className="flex gap-2 items-center">
                            <Checkbox checked={allSelected} onChange={() => selectAll()} />
                            {t('dateLabel')}
                        </TableHead>
                        <TableHead className="whitespace-nowrap">{t('receiptLabel')}</TableHead>
                        <TableHead>{t('typeLabel')}</TableHead>
                        <TableHead>{t('amountLabel')}</TableHead>
                        <TableHead>{t('noteLabel')}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody className="hover:cursor-pointer">
                    {data?.balanceEntries?.edges?.length > 0 &&
                        normalizeArray(data.balanceEntries).map((transaction: any) => (
                            <TableRow key={transaction.id} className="text-zinc-700 hover:bg-rose-50">
                                <TableCell onClick={() => selectConsumption(transaction.id)}>
                                    <div className="flex gap-2 items-center w-full">
                                        <Checkbox
                                            onChange={() => selectConsumption(transaction.id)}
                                            checked={selectedConsumptions.includes(transaction.id)}
                                        />
                                        {dayjs(transaction.date).format('DD.MM.YYYY')}
                                    </div>
                                </TableCell>
                                <TableCell onClick={() => selectConsumption(transaction.id)}>
                                    {transaction.invoiceNumber}
                                </TableCell>
                                <TableCell onClick={() => selectConsumption(transaction.id)}>
                                    {t(transaction.type)}
                                </TableCell>

                                <TableCell onClick={() => selectConsumption(transaction.id)}>
                                    <div className="flex items-center justify-between gap-2 max-w-[150px] min-w-[max-content]">
                                        <Balance
                                            classname={
                                                POSITIVE_TRANSACTIONS.includes(transaction.type)
                                                    ? 'text-green-700'
                                                    : 'text-red-700'
                                            }
                                            balance={{ amount: transaction.amount, currency: 'CHF' }}
                                        />
                                        <div
                                            className={`w-6 h-6 rounded-full flex items-center justify-center text-md font-bold
                                                ${
                                                    POSITIVE_TRANSACTIONS.includes(transaction.type)
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                }`}
                                        >
                                            <div>{POSITIVE_TRANSACTIONS.includes(transaction.type) ? '+' : '–'}</div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell onClick={() => selectConsumption(transaction.id)}>
                                    {transaction.note}
                                </TableCell>
                                <TableCell className="py-3 justify-end flex">
                                    <MemberConsumptionsActions
                                        user={user}
                                        transaction={transaction}
                                        onDeduct={() => deductFromRemainder(transaction)}
                                        onEdit={() => editConsumption(transaction)}
                                        onDelete={() => {
                                            setEditingConsumption(transaction);
                                            setIsOpenDelete(true);
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {data?.balanceEntries?.totalCount === 0 && (
                <div className={'py-5 text-center text-zinc-700'}>{t('noTransactions')}</div>
            )}
            <Pagination pageInfo={data?.balanceEntries?.pageInfo} total={data?.balanceEntries?.totalCount} />
            <ConfirmConsumptionsModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onConfirm={() => confirmExportConsumptions()}
                isLoading={loading}
            />
            <DeleteConsumptionsModal
                isOpen={isOpenDelete}
                onClose={() => setIsOpenDelete(false)}
                onConfirm={deleteConsumption}
                isLoading={isLoading}
            />
            <DeductRemainderConfirmModal
                isOpen={isOpenDeduct}
                onClose={() => setIsOpenDeduct(false)}
                transaction={deductTransaction}
                onConfirm={confirmDeduct}
            />
            <AddConsumptionModal
                isOpen={isOpenAdd}
                onClose={() => handleConsumptionModalClose()}
                onAdd={handleConsumptionAdd}
                editConsumption={editingConsumption}
                isLoading={isLoading}
                member={user}
            />
        </div>
    );
};
